import { useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  ButtonGroupSeparator,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@@/ui/primitives';
import {
  BracesIcon,
  BracketsIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FolderCodeIcon,
  HashIcon,
  Link2Icon,
  ListCheckIcon,
  LockIcon,
  MegaphoneIcon,
  ToggleRightIcon,
  TypeIcon
} from 'lucide-react';
import { PropertyEditor } from './property-editor';
import { Field } from '../../schemas/entity';
import { cn } from '@@/lib/utils';

export function PropertyListItem({
  name,
  property,
  onSave,
  onDelete,
  onMoveUp,
  onMoveDown,
  existingNames,
  currentPrimaryField,
  allFields,
  isFirst,
  isLast,
  hasItems,
  hasSubEntities
}: {
  name: string;
  property: Field;
  onSave: (name: string, field: Field) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  existingNames: string[];
  currentPrimaryField?: string;
  allFields?: Record<string, Field>;
  isFirst: boolean;
  isLast: boolean;
  hasItems: boolean;
  hasSubEntities: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const isLocked = property.inherited;

  const handleSave = (newName: string, newProperty: Field) => {
    onSave(newName, newProperty);
    setIsEditing(false);
  };

  const handleMoveUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoveUp();
  };

  const handleMoveDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoveDown();
  };

  const fieldTypeMap: Record<string, string> = {
    string: 'String',
    number: 'Number',
    boolean: 'Boolean',
    date_time: 'Datetime',
    json: 'JSON',
    yaml: 'YAML',
    connect_resource: 'Connect',
    prompt: 'Prompt',
    picklist: 'Pick List',
    reference: 'Reference',
    object: 'Object'
  };

  const fieldTypeIconMap: Record<string, React.ElementType> = {
    string: TypeIcon,
    number: HashIcon,
    boolean: ToggleRightIcon,
    date_time: CalendarIcon,
    json: BracesIcon,
    yaml: BracketsIcon,
    connect_resource: FolderCodeIcon,
    prompt: MegaphoneIcon,
    picklist: ListCheckIcon,
    reference: Link2Icon,
    object: BracesIcon
  };
  const FieldIcon = fieldTypeIconMap[property.definition.type];

  return (
    <Collapsible
      open={isEditing}
      onOpenChange={setIsEditing}
      disabled={isLocked}
      className={cn(isLocked && 'bg-neutral-50')}
    >
      <CollapsibleTrigger
        asChild
        className={cn(
          'flex items-center justify-between h-10 w-full',
          !isLocked && 'hover:cursor-pointer hover:bg-neutral-50'
        )}
      >
        <div>
          <div className='flex-1 flex gap-1 flex-col py-1 px-4'>
            <div className='flex items-center gap-2'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-2 text-muted-foreground pr-2'>
                    <FieldIcon className='size-3.5 mt-px shrink-0' />

                    {isLocked && <LockIcon className='size-3.5 shrink-0' />}
                  </div>
                </TooltipTrigger>
                <TooltipContent side='right'>
                  <span className='font-medium'>{fieldTypeMap[property.definition.type]}</span>
                </TooltipContent>
              </Tooltip>
              <h4 className='text-sm text-foreground font-[450]'>{name}</h4>
              {property.inherited && (
                <Badge variant='secondary' className='text-xs rounded-sm'>
                  Inherited
                </Badge>
              )}
              <Badge variant='outline' className='text-xs rounded-sm mt-px'>
                {fieldTypeMap[property.definition.type]}
              </Badge>
            </div>
          </div>
          <div className='flex gap-2 items-center px-2'>
            {property.primary && <Badge className='text-xs rounded-sm'>Primary</Badge>}
            {property.definition.required && (
              <Badge variant='destructive' className='text-xs bg-red-50 text-red-950 border-red-50 rounded-sm'>
                Required
              </Badge>
            )}
            {property.conditions !== null && property.conditions.groups.length > 0 && (
              <Badge variant='outline' className='text-xs bg-orange-100 text-orange-950 border-orange-50 rounded-sm'>
                Conditional
              </Badge>
            )}
            {!isLocked && (
              <div className='flex flex-row gap-2 pl-2 text-muted-foreground'>
                <ButtonGroup className='shadow-none!'>
                  <Button
                    size='xs'
                    variant='outline'
                    className='rounded-sm shadow-none!'
                    onClick={handleMoveUp}
                    disabled={isFirst}
                  >
                    <ChevronUpIcon className='size-4' />
                  </Button>
                  <ButtonGroupSeparator />
                  <Button
                    size='xs'
                    variant='outline'
                    className='rounded-sm shadow-none!'
                    onClick={handleMoveDown}
                    disabled={isLast}
                  >
                    <ChevronDownIcon className='size-4' />
                  </Button>
                </ButtonGroup>
              </div>
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className='flex flex-col gap-4 my-2'>
        <PropertyEditor
          onSave={handleSave}
          onDelete={onDelete}
          existingNames={existingNames.filter((n) => n !== name)}
          initialName={name}
          initialProperty={property}
          currentPrimaryField={currentPrimaryField}
          allFields={allFields}
          hasItems={hasItems}
          hasSubEntities={hasSubEntities}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
